import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

const ModelManagement = () => {
  const [models, setModels] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [activeModelId, setActiveModelId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteModelDialogOpen, setDeleteModelDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  // Form state for adding/editing models
  const [formData, setFormData] = useState({
    model_id: '',
    model_name: '',
    model_type: 'text-generation',
    provider: '',
    description: '',
    cost_per_input_token: '',
    cost_per_output_token: '',
    max_tokens: 4000,
    api_format: 'titan'
  });

  useEffect(() => {
    loadModels();
    loadAvailableModels();
  }, []);

  const loadModels = async () => {
    try {
      setError('');
      const response = await apiCall('/api/ai/models/');
      setModels(response.data.models);
      setActiveModelId(response.data.active_model_id);
    } catch (error) {
      console.error('Error loading models:', error);
      setError('Failed to load models');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const response = await apiCall('/api/ai/models/available', { requireAuth: false });
      setAvailableModels(response.data.available_models);
    } catch (error) {
      console.error('Error loading available models:', error);
    }
  };

  const handleSetActive = async (modelId) => {
    try {
      setError('');
      await apiCall(`/api/ai/models/${modelId}/activate`, { method: 'POST' });
      await loadModels(); // Refresh the list
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error setting active model:', error);
      setError('Failed to set active model');
    }
  };

  const handleDelete = (modelId) => {
    const model = models.find(m => m.id === modelId);
    setModelToDelete(model);
    setDeleteModelDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError('');
      await apiCall(`/api/ai/models/${modelToDelete.id}`, { method: 'DELETE' });
      await loadModels(); // Refresh the list
      setDeleteModelDialogOpen(false);
      setModelToDelete(null);
    } catch (error) {
      console.error('Error deleting model:', error);
      setError('Failed to delete model');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModelDialogOpen(false);
    setModelToDelete(null);
  };

  const handleAddModel = () => {
    setEditingModel(null);
    setFormData({
      model_id: '',
      model_name: '',
      model_type: 'text-generation',
      provider: '',
      description: '',
      cost_per_input_token: '',
      cost_per_output_token: '',
      max_tokens: 4000,
      api_format: 'titan'
    });
    setShowAddForm(true);
  };

  const handleEditModel = (model) => {
    setEditingModel(model);
    setFormData({
      model_id: model.model_id,
      model_name: model.model_name,
      model_type: model.model_type,
      provider: model.provider,
      description: model.description,
      cost_per_input_token: model.cost_per_input_token,
      cost_per_output_token: model.cost_per_output_token,
      max_tokens: model.max_tokens,
      api_format: model.api_format
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      if (editingModel) {
        await apiCall(`/api/ai/models/${editingModel.id}`, {
          method: 'PUT',
          body: formData
        });
      } else {
        await apiCall('/api/ai/models/', {
          method: 'POST',
          body: formData
        });
      }
      
      setShowAddForm(false);
      await loadModels(); // Refresh the list
    } catch (error) {
      console.error('Error saving model:', error);
      setError('Failed to save model');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            AI Model Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddModel}
          >
            Add Model
          </Button>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Models Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configured Models
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Model Name</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Cost (Input/Output)</TableCell>
                    <TableCell>Max Tokens</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map((model) => (
                    <TableRow key={model.id}>
                      <TableCell>
                        <IconButton
                          onClick={() => handleSetActive(model.id)}
                          color={model.id === activeModelId ? 'success' : 'default'}
                          size="small"
                        >
                          {model.id === activeModelId ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {model.model_name}
                        </Typography>
                        {model.description && (
                          <Typography variant="caption" color="text.secondary">
                            {model.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={model.provider} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={model.model_type.replace('-', ' ')} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${model.cost_per_input_token} / ${model.cost_per_output_token}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {model.max_tokens.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditModel(model)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(model.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Available Models */}
        {availableModels.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Models
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These models can be added to your configuration
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {availableModels.map((model, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {model.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {model.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={model.provider} size="small" variant="outlined" />
                          <Chip label={model.type} size="small" color="primary" variant="outlined" />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Add/Edit Model Dialog */}
      <Dialog 
        open={showAddForm} 
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingModel ? 'Edit Model' : 'Add New Model'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  name="model_id"
                  label="Model ID"
                  value={formData.model_id}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
                <TextField
                  name="model_name"
                  label="Model Name"
                  value={formData.model_name}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Model Type</InputLabel>
                  <Select
                    name="model_type"
                    value={formData.model_type}
                    onChange={handleInputChange}
                    label="Model Type"
                  >
                    <MenuItem value="text-generation">Text Generation</MenuItem>
                    <MenuItem value="chat">Chat</MenuItem>
                    <MenuItem value="embedding">Embedding</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  name="provider"
                  label="Provider"
                  value={formData.provider}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Box>

              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <TextField
                  name="cost_per_input_token"
                  label="Cost per Input Token"
                  type="number"
                  value={formData.cost_per_input_token}
                  onChange={handleInputChange}
                  inputProps={{ step: 0.000001, min: 0 }}
                  fullWidth
                />
                <TextField
                  name="cost_per_output_token"
                  label="Cost per Output Token"
                  type="number"
                  value={formData.cost_per_output_token}
                  onChange={handleInputChange}
                  inputProps={{ step: 0.000001, min: 0 }}
                  fullWidth
                />
                <TextField
                  name="max_tokens"
                  label="Max Tokens"
                  type="number"
                  value={formData.max_tokens}
                  onChange={handleInputChange}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Box>

              <FormControl fullWidth>
                <InputLabel>API Format</InputLabel>
                <Select
                  name="api_format"
                  value={formData.api_format}
                  onChange={handleInputChange}
                  label="API Format"
                >
                  <MenuItem value="titan">Titan</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingModel ? 'Update Model' : 'Add Model'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Model Confirmation Dialog */}
      <Dialog
        open={deleteModelDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-model-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-model-dialog-title">
          Delete Model
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{modelToDelete?.model_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
            </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ModelManagement;
